import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import { Search, Calendar, Download, Copy, Check, Star } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ToolHistory = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTool, setFilterTool] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    filterSessions();
  }, [searchTerm, filterTool, sessions]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/tools/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSessions = () => {
    let filtered = sessions;

    if (filterTool !== 'all') {
      filtered = (Array.isArray(filtered) ? filtered : []).filter(s => s.tool_id === filterTool || s.tool_type === filterTool);
    }

    if (searchTerm) {
      const q = ((searchTerm) == null ? '' : String(searchTerm)).toLowerCase();
      filtered = (Array.isArray(filtered) ? filtered : []).filter(s =>
        (s.tool_name || '').toLowerCase().includes(q) ||
        (s.tool_id || '').toLowerCase().includes(q) ||
        (s.tool_type || '').toLowerCase().includes(q)
      );
    }

    setFilteredSessions(filtered);
  };

  const viewSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/tools/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const messages = response.data.messages;
      const assistantMessage = (Array.isArray(messages) ? messages : []).find(m => m.role === 'assistant');
      
      if (assistantMessage) {
        // Navigate to appropriate tool with result
        const toolRoutes = {
          roadmap: '/portal/roadmap-creator',
          agent: '/portal/agent-builder',
          process: '/portal/process-optimizer',
          training: '/portal/virtual-training'
        };
        
        const route = toolRoutes[response.data.session.tool_type];
        if (route) {
          navigate(route, { state: { result: assistantMessage.content } });
        }
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const exportToPDF = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/tools/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const session = response.data.session;
      const messages = response.data.messages;
      const assistantMessage = (Array.isArray(messages) ? messages : []).find(m => m.role === 'assistant');
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text('KRYPTON9', 20, 20);
      doc.setFontSize(12);
      doc.text(`Tool: ${session.tool_type.charAt(0).toUpperCase() + session.tool_type.slice(1)}`, 20, 30);
      doc.text(`Date: ${new Date(session.created_at).toLocaleDateString()}`, 20, 37);
      
      // Content
      doc.setFontSize(10);
      const splitText = doc.splitTextToSize(assistantMessage?.content || 'No content', 170);
      doc.text(splitText, 20, 50);
      
      // Save
      doc.save(`krypton9-${session.tool_type}-${sessionId.slice(0, 8)}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const copyToClipboard = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/tools/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const messages = response.data.messages;
      const assistantMessage = (Array.isArray(messages) ? messages : []).find(m => m.role === 'assistant');
      
      await navigator.clipboard.writeText(assistantMessage?.content || '');
      setCopiedId(sessionId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error copying:', error);
    }
  };

  const rateSession = async (sessionId, rating) => {
    try {
      await axios.post(
        `${API}/analytics/rate-tool?session_id=${sessionId}&rating=${rating}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(lang === 'nl' ? 'Bedankt voor je feedback!' : 'Thank you for your feedback!');
    } catch (error) {
      console.error('Error rating:', error);
    }
  };

  const toolName = (session) => {
    if (session.tool_name) return session.tool_name;
    const id = session.tool_id || session.tool_type || '';
    return t.tools[id]?.title || t.portalTools?.find(p => p.id === id)?.title || id.charAt(0).toUpperCase() + id.slice(1);
  };

  // Get unique tool_ids from sessions for filter dropdown
  const uniqueTools = [...new Set((Array.isArray(sessions) ? sessions : []).map(s => s.tool_id || s.tool_type).filter(Boolean))];

  const L = lang === 'nl' ? {
    back: 'Terug naar Portaal',
    title: 'Tool Geschiedenis',
    search: 'Zoek sessies...',
    allTools: 'Alle Tools',
    view: 'Bekijk',
    rate: 'Beoordeel dit resultaat:',
    empty: 'Nog geen sessies gevonden. Begin met AI tools om je geschiedenis op te bouwen!',
    loading: 'Geschiedenis laden...',
    roadmap: 'Roadmap Creator',
    agent: 'Agent Builder',
    process: 'Procesoptimalisatie',
    training: 'Virtuele Training',
  } : {
    back: 'Back to Portal',
    title: 'Tool History',
    search: 'Search sessions...',
    allTools: 'All Tools',
    view: 'View',
    rate: 'Rate this result:',
    empty: 'No history yet — your activity will appear here as you use the tools.',
    loading: 'Loading history...',
    roadmap: 'Roadmap Creator',
    agent: 'Agent Builder',
    process: 'Process Optimizer',
    training: 'Virtual Training',
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>{L.loading}</p>
      </div>
    );
  }

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">{L.title}</h1>
      </div>

      <div className="history-container">
        {/* Search and Filters */}
        <div className="history-filters">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder={L.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <select
            value={filterTool}
            onChange={(e) => setFilterTool(e.target.value)}
            className="filter-select"
          >
            <option value="all">{L.allTools}</option>
            {(Array.isArray(uniqueTools) ? uniqueTools : []).map(tid => (
              <option key={tid} value={tid}>{(Array.isArray(sessions) ? sessions : []).find(s => (s.tool_id || s.tool_type) === tid)?.tool_name || tid}</option>
            ))}
          </select>
        </div>

        {/* Sessions List */}
        {filteredSessions.length === 0 ? (
          <div className="empty-state">
            <p className="body-large">{L.empty}</p>
          </div>
        ) : (
          <div className="sessions-list">
            {(Array.isArray(filteredSessions) ? filteredSessions : []).map((session, idx) => (
              <div key={session.session_id || `usage-${idx}`} className="session-card">
                <div className="session-header">
                  <div>
                    <h3 className="heading-3">
                      {toolName(session)}
                    </h3>
                    <p className="caption">
                      <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                      {session.timestamp ? new Date(session.timestamp).toLocaleString('nl-NL') : ''}
                    </p>
                  </div>
                  
                  <div className="session-actions">
                    {session.session_id && (
                      <>
                        <button
                          onClick={() => copyToClipboard(session.session_id)}
                          className="action-btn"
                          title={lang === 'nl' ? 'Kopieer naar klembord' : 'Copy to clipboard'}
                        >
                          {copiedId === session.session_id ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                        <button
                          onClick={() => exportToPDF(session.session_id)}
                          className="action-btn"
                          title={lang === 'nl' ? 'Exporteer als PDF' : 'Export as PDF'}
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => viewSession(session.session_id)}
                          className="btn-primary"
                          style={{ minHeight: '2rem', padding: '0.5rem 1rem' }}
                        >
                          {L.view}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {session.session_id && (
                  <div className="session-rating">
                    <span className="body-small">{L.rate}</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => rateSession(session.session_id, star)}
                        className="star-btn"
                      >
                        <Star size={16} fill={star <= 3 ? '#BCA182' : 'none'} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolHistory;
